import { useEffect, useState } from 'react';

import { fetchWinningValue, type WinningValue } from '../lib/api';

/**
 * What one block is worth right now.
 *
 * FETCHED ONCE PER PAGE LOAD, not per configuration. Unlike the odds, this figure
 * depends on nothing the customer has chosen — not hashrate, term, or mining mode — so
 * there is no race to guard and no reason to re-ask while somebody drags the slider.
 * That is also why it is a separate endpoint from `/odds`.
 *
 * FAILURE IS SILENT AND THE FIGURE SIMPLY DISAPPEARS. There is no error state and no
 * retry: the value is supplementary to the odds, and an error message about a price feed
 * beside a purchase decision would be noise. The server already answers `available:
 * false` rather than erroring when the price cannot be fetched; a network failure here
 * is treated identically.
 *
 * The browser never computes this. It receives cents and formats them.
 */
export function useWinningValue(): WinningValue | null {
  const [value, setValue] = useState<WinningValue | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchWinningValue(controller.signal)
      .then(setValue)
      .catch(() => {
        if (controller.signal.aborted) return;
        // Indistinguishable from the server saying it has no price, and treated the
        // same: show nothing rather than something invented.
        setValue({ available: false });
      });

    return () => controller.abort();
  }, []);

  return value;
}
